import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, MessageCircle } from 'lucide-react';
import * as THREE from 'three';
import Icon from '../../../components/AppIcon';
import { PortfolioService } from '../../../services/portfolioService';

const LogoLoader = ({ progress }) => {
  const LogoContent = ({ isColored }) => (
    <div className={`relative flex items-center justify-center gap-6 ${!isColored ? 'grayscale opacity-20' : ''}`}>
      <div className="relative">
        <div className={`relative w-16 h-16 rounded-lg flex items-center justify-center ${isColored ? 'bg-gradient-to-br from-blue-400 to-blue-600 shadow-2xl shadow-blue-500/50' : 'bg-gray-800'}`}>
          <Icon name="Code2" size={32} color="white" strokeWidth={2.5} />
        </div>
        {isColored && <div className="absolute -top-1 -right-1 w-4 h-4 bg-cyan-400 rounded-full animate-pulse" />}
      </div>
      <div className="flex flex-col text-left text-white">
        <h2 className={`text-5xl font-bold leading-none tracking-tight ${isColored ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600' : 'text-gray-500'}`}>
          Mustan-Dev
        </h2>
        <p className={`text-base font-medium tracking-wide mt-2 ${isColored ? 'text-white' : 'text-gray-600'}`}>
          Software Engineer & Developer
        </p>
      </div>
    </div>
  );

  return (
    <div className="relative">
      <LogoContent isColored={false} />
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - progress}% 0 0)` }}>
        <LogoContent isColored={true} />
      </div>
    </div>
  );
};

const Modern3DBackground = () => {
  const mountRef = React.useRef(null);

  useEffect(() => {
    let camera, scene, renderer, particleSystem;
    let animationId;
    const mount = mountRef.current;
    if (!mount) return;

    renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'high-performance' });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    renderer.domElement.style.position = 'absolute';
    renderer.domElement.style.top = '0';
    renderer.domElement.style.left = '0';
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    mount.appendChild(renderer.domElement);

    scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 1, 2000);
    camera.position.set(0, 0, 450);

    const createHelix = () => {
      const strandCount = 3;
      const particlesPerStrand = 800;
      const totalParticles = strandCount * particlesPerStrand + 2000;
      const geometry = new THREE.BufferGeometry();
      const positions = new Float32Array(totalParticles * 3);
      const colors = new Float32Array(totalParticles * 3);
      const sizes = new Float32Array(totalParticles);

      const color1 = new THREE.Color(0x3b82f6);
      const color2 = new THREE.Color(0x06b6d4);
      const color3 = new THREE.Color(0xffffff);
      let idx = 0;

      for (let s = 0; s < strandCount; s++) {
        const offset = (s * Math.PI * 2) / strandCount;
        const radius = 100 + Math.sin(s) * 20;
        for (let i = 0; i < particlesPerStrand; i++) {
          const t = i / particlesPerStrand;
          const angle = t * Math.PI * 2 * 4 + offset;
          const height = 1200;
          const x = Math.cos(angle) * radius;
          const y = (t - 0.5) * height;
          const z = Math.sin(angle) * radius;
          const jitter = 12 + Math.sin(t * 20) * 8;
          positions[idx * 3] = x + (Math.random() - 0.5) * jitter;
          positions[idx * 3 + 1] = y + (Math.random() - 0.5) * jitter;
          positions[idx * 3 + 2] = z + (Math.random() - 0.5) * jitter;
          const mixedColor = Math.random() > 0.9 ? color3.clone().lerp(color1, Math.random() * 0.5) : color1.clone().lerp(color2, t);
          colors[idx * 3] = mixedColor.r;
          colors[idx * 3 + 1] = mixedColor.g;
          colors[idx * 3 + 2] = mixedColor.b;
          sizes[idx] = (Math.random() * 2 + 0.5) * (1 + Math.sin(t * 10) * 0.3);
          idx++;
        }
      }

      for (let i = idx; i < totalParticles; i++) {
        const r = 600 + Math.random() * 200;
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(2 * Math.random() - 1);
        positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
        positions[i * 3 + 1] = (Math.random() - 0.5) * 1600;
        positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
        const dustColor = Math.random() > 0.5 ? color1 : color2;
        colors[i * 3] = dustColor.r * 0.3;
        colors[i * 3 + 1] = dustColor.g * 0.3;
        colors[i * 3 + 2] = dustColor.b * 0.3;
        sizes[i] = Math.random() * 1.5 + 0.3;
      }

      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

      const material = new THREE.PointsMaterial({
        size: 2.5,
        vertexColors: true,
        transparent: true,
        opacity: 0.9,
        blending: THREE.AdditiveBlending,
        sizeAttenuation: true,
        depthWrite: false
      });

      particleSystem = new THREE.Points(geometry, material);
      scene.add(particleSystem);
      return particleSystem;
    };

    particleSystem = createHelix();
    const pointLight = new THREE.PointLight(0x3b82f6, 2, 1000);
    pointLight.position.set(0, 0, 0);
    scene.add(pointLight);
    scene.add(new THREE.AmbientLight(0x404040, 0.5));

    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    const onMouseMove = (e) => {
      const mouseX = (e.clientX / window.innerWidth) * 2 - 1;
      const mouseY = -(e.clientY / window.innerHeight) * 2 + 1;
      if (particleSystem) {
        particleSystem.rotation.x += (mouseY * 0.1 - particleSystem.rotation.x) * 0.03;
        particleSystem.rotation.z += (mouseX * 0.1 - particleSystem.rotation.z) * 0.03;
      }
    };

    const clock = new THREE.Clock();
    const animate = () => {
      animationId = requestAnimationFrame(animate);
      const time = clock.getElapsedTime();
      const scrollY = window.scrollY;
      if (particleSystem) {
        particleSystem.rotation.y = time * 0.15;
        const pulse = 1 + Math.sin(time * 0.5) * 0.02;
        particleSystem.scale.setScalar(pulse);
      }
      const targetY = scrollY * 0.5;
      const targetZ = 450 - Math.abs(scrollY * 0.15);
      camera.position.y += (-targetY - camera.position.y) * 0.05;
      camera.position.z += (targetZ - camera.position.z) * 0.05;
      renderer.render(scene, camera);
    };

    animate();
    window.addEventListener('resize', onResize);
    window.addEventListener('mousemove', onMouseMove);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('mousemove', onMouseMove);
      cancelAnimationFrame(animationId);
      if (particleSystem) {
        particleSystem.geometry.dispose();
        particleSystem.material.dispose();
      }
      if (renderer) {
        renderer.dispose();
        if (mount.contains(renderer.domElement)) {
          mount.removeChild(renderer.domElement);
        }
      }
    };
  }, []);

  return (
    <div
      ref={mountRef}
      className="absolute inset-0 z-0 bg-black -top-16 w-full h-full overflow-hidden"
      style={{
        pointerEvents: 'none',
        background: 'radial-gradient(ellipse at center, #0c0c16 0%, #000000 70%, #050505 100%)',
        left: 0,
        right: 0,
        width: '100%'
      }}
    />
  );
};

const HeroContent = () => {
  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: 'easeOut' } }
  };

  const skills = [
    'Full-Stack Development',
    'React & TypeScript',
    'Node.js & Express',
    'Mobile Apps (React Native)',
    'Cloud & DevOps (AWS, Docker)',
    'Open Source',
    'Microservices',
    'AI/ML',
    'Three.js',
    'Storybook'
  ];

  const stats = [
    { label: 'Years Experience', value: '4+' },
    { label: 'Projects Completed', value: '50+' },
    { label: 'Happy Clients', value: '30+' },
    { label: 'Technologies Used', value: '20+' }
  ];

  return (
    <motion.div initial="hidden" animate="visible" className="max-w-6xl mx-auto w-full flex flex-col items-center text-center pb-24">
      <motion.h1 variants={itemVariants} className="relative mb-6 text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl">
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-cyan-400 to-blue-100 drop-shadow-[0_0_30px_rgba(59,130,246,0.8),0_0_60px_rgba(6,182,212,0.6)]">
          Mustan Aliev
        </span>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[150%] bg-gradient-to-r from-blue-500/20 to-cyan-400/20 blur-[50px] -z-10 rounded-lg animate-pulse" />
      </motion.h1>
      <motion.p variants={itemVariants} className="px-4 text-sm leading-relaxed text-slate-300 sm:text-base md:text-lg lg:text-xl mb-10 max-w-3xl">
        Software Engineer & Open Source Developer. Building scalable, user-focused web and mobile applications with React, Node.js, TypeScript, and cloud technologies. Passionate about open source, automation, and creative problem-solving.
      </motion.p>
      <motion.div variants={itemVariants} className="mb-14 flex flex-wrap justify-center gap-3 max-w-5xl">
        {skills.map((skill, index) => (
          <span key={index} className="cursor-default rounded-full border border-slate-700 bg-[#1e293b]/80 px-5 py-2.5 text-sm font-medium text-slate-200 shadow-sm backdrop-blur-sm transition-all hover:bg-slate-800">
            {skill}
          </span>
        ))}
      </motion.div>
      <motion.div variants={itemVariants} className="flex flex-col items-center justify-center gap-5 mb-20 sm:flex-row">
        <motion.a
          href="/contact"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="group flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#3b82f6] to-[#8b5cf6] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 transition-all sm:px-8 sm:py-3.5"
        >
          Get In Touch
          <MessageCircle className="h-4 w-4 sm:h-5 sm:w-5" />
        </motion.a>
        <motion.a
          href="/portfolio"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex items-center justify-center gap-2 rounded-full border border-white bg-transparent px-6 py-3 text-sm font-semibold text-white transition-all backdrop-blur-md hover:bg-white/10 sm:px-8 sm:py-3.5"
        >
          View My Work
          <Briefcase className="h-4 w-4 sm:h-5 sm:w-5" />
        </motion.a>
      </motion.div>
      <motion.div variants={itemVariants} className="grid w-full max-w-4xl grid-cols-2 gap-4 pt-4 px-4 sm:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="flex flex-col items-center">
            <span className="mb-2 text-2xl font-bold text-white sm:text-3xl md:text-4xl">{stat.value}</span>
            <span className="text-xs font-medium uppercase tracking-wide text-slate-400 sm:text-sm">{stat.label}</span>
          </div>
        ))}
      </motion.div>
    </motion.div>
  );
};

const HeroSection = () => {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [systemReady, setSystemReady] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  useEffect(() => {
    PortfolioService?.trackPageView('/');
    const startTime = Date.now();
    const duration = 2500;
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(100, (elapsed / duration) * 100);
      setLoadingProgress(progress);
      if (progress >= 100) {
        clearInterval(interval);
        setTimeout(() => {
          setSystemReady(true);
          setShowScrollIndicator(true);
        }, 500);
      }
    }, 16);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setShowScrollIndicator(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <AnimatePresence>
        {!systemReady && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black"
          >
            <LogoLoader progress={loadingProgress} />
          </motion.div>
        )}
      </AnimatePresence>
      <section className="relative -top-16 w-screen min-h-[calc(100vh+64px)] flex items-center justify-center overflow-hidden pt-16">
        <Modern3DBackground />

        <div className="relative z-10 w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <HeroContent />
        </div>

        {showScrollIndicator && (
          <div className="absolute bottom-12 left-1/2 z-20 flex -translate-x-1/2 flex-col items-center pointer-events-none sm:bottom-16">
            <span className="mb-2 text-xs font-medium text-white/70">Scroll to explore</span>
            <div className="h-8 w-0.5 bg-gradient-to-b from-white/70 to-transparent animate-bounce" />
          </div>
        )}
      </section>
    </>
  );
};

export default HeroSection;
